"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  AlignmentText,
  ScoreMatrix,
} from "@/components/tools/biology-genetics/alignment-parts";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type AlignMode,
  type Alignment,
  align,
  alignmentStats,
  dnaScoring,
  formatAlignment,
  linearMatrix,
  MAX_CELLS,
  pathCells,
  proteinScoring,
  type Scoring,
} from "@/lib/tool-kit/biology-genetics/alignment";
import { parseFasta } from "@/lib/tool-kit/biology-genetics/fasta";
import {
  cleanResidues,
  detectKind,
  type SequenceKind,
} from "@/lib/tool-kit/biology-genetics/sequence-input";
import {
  MATRIX_NAMES,
  type MatrixName,
} from "@/lib/tool-kit/biology-genetics/substitution-matrices";

/** English strings. A translated content file overrides these through `ui`. */
export const SEQUENCE_ALIGNMENT_TOOL_STRINGS = {
  firstLabel: "Sequence 1 — plain text or FASTA",
  secondLabel: "Sequence 2 — plain text or FASTA",
  inputHint:
    "Only the first FASTA record of each box is used. Spaces, line breaks and numbers are ignored.",
  firstPlaceholder: ">first_sequence\nMVLSPADKTNVKAAWGKV…",
  secondPlaceholder: ">second_sequence\nMVHLTPEEKSAVTALWGKV…",
  exampleProtein: "Example: human α- and β-globin",
  exampleDna: "Example: a textbook DNA pair",
  proteinHeaderA: "HBA_HUMAN Hemoglobin subunit alpha (UniProt P69905)",
  proteinHeaderB: "HBB_HUMAN Hemoglobin subunit beta (UniProt P68871)",
  dnaHeaderA: "seq1",
  dnaHeaderB: "seq2",
  modeLabel: "Alignment type",
  modeGlobal: "Global (Needleman–Wunsch) — align the whole of both",
  modeLocal: "Local (Smith–Waterman) — find the best matching region",
  kindLabel: "Sequence type",
  kindAuto: "Detect automatically",
  kindDna: "DNA or RNA",
  kindProtein: "Protein",
  scoringHeading: "Scoring",
  matchLabel: "Match score",
  mismatchLabel: "Mismatch score",
  matrixLabel: "Substitution matrix",
  gapOpenLabel: "Gap open penalty",
  gapExtendLabel: "Gap extension penalty",
  gapHint:
    "Enter penalties as positive numbers. A gap of length k costs open + (k − 1) × extension; set both equal for a linear gap penalty.",
  detectedDna: "Treated as DNA or RNA.",
  detectedProtein: "Treated as protein.",
  results: "Results",
  score: "Alignment score",
  identity: "Identity",
  similarity: "Similarity",
  gaps: "Gaps",
  identityValue: "{count}/{total} ({percent}%)",
  columns: "Alignment length",
  mismatchesLabel: "Mismatches",
  regionLabel: "{name}: residues {from}–{to} of {length}",
  alignmentHeading: "Alignment",
  alignmentAria: "Pairwise alignment of {first} and {second}",
  legend:
    "| identical · : similar (positive score) · . different · blank = gap. One of the best alignments is shown; others may tie.",
  legendDna:
    "| identical · . different · blank = gap. One of the best alignments is shown; others may tie.",
  copyAlignment: "Copy alignment",
  copied: "Copied!",
  matrixToggle: "Show the scoring matrix",
  matrixCaption:
    "Dynamic-programming score matrix; the cells on the best path are shaded",
  matrixNote:
    "Available for short sequences with a linear gap penalty (open = extension).",
  unnamed: "Sequence {n}",
  noLocal:
    "No region scores above zero with these settings, so there is no local alignment.",
  prompt: "Enter two sequences to align them.",
  errorEmpty: "Sequence {n} has no usable letters.",
  errorNumbers:
    "Enter every score and penalty as a number; the gap penalties must be 0 or more.",
  errorTooLarge:
    "These sequences are too long to align in the browser (their lengths multiplied must stay under {limit}).",
  dropped: "Ignored characters that are not valid residues: {list}.",
} as const;

const PROTEIN_A =
  "MVLSPADKTNVKAAWGKVGAHAGEYGAEALERMFLSFPTTKTYFPHFDLSHGSAQVKGHGKKVADALTNAVAHVDDMPNALSALSDLHAHKLRVDPVNFKLLSHCLLVTLAAHLPAEFTPAVHASLDKFLASVSTVLTSKYR";
const PROTEIN_B =
  "MVHLTPEEKSAVTALWGKVNVDEVGGEALGRLLVVYPWTQRFFESFGDLSTPDAVMGNPKVKAHGKKVLGAFSDGLAHLDNLKGTFATLSELHCDKLHVDPENFRLLGNVLVCVLAHHFGKEFTPPVQAAYQKVVAGVANALAHKYH";

type KindChoice = "auto" | SequenceKind;

const MATRIX_LIMIT = 20;

interface Fields {
  match: string;
  mismatch: string;
  matrix: MatrixName;
  open: string;
  extend: string;
}

const PROTEIN_FIELDS: Fields = {
  match: "5",
  mismatch: "-4",
  matrix: "BLOSUM62",
  open: "10",
  extend: "0.5",
};
const DNA_FIELDS: Fields = {
  match: "1",
  mismatch: "-1",
  matrix: "BLOSUM62",
  open: "1",
  extend: "1",
};

function buildScoring(fields: Fields, kind: SequenceKind): Scoring | null {
  const open = parseNumberInput(fields.open);
  const extend = parseNumberInput(fields.extend);
  if (open === null || extend === null || open < 0 || extend < 0) {
    return null;
  }
  if (kind === "protein") {
    return proteinScoring(fields.matrix, open, extend);
  }
  const match = parseNumberInput(fields.match);
  const mismatch = parseNumberInput(fields.mismatch);
  return match === null || mismatch === null
    ? null
    : dnaScoring(match, mismatch, open, extend);
}

function percent(
  count: number,
  total: number,
  fmt: (v: number, o?: Intl.NumberFormatOptions) => string
): string {
  return total === 0
    ? "0"
    : fmt((count / total) * 100, { maximumFractionDigits: 1 });
}

type Strings = typeof SEQUENCE_ALIGNMENT_TOOL_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

function ScoringPanel({
  strings,
  kind,
  kindChoice,
  onKind,
  fields,
  onField,
}: {
  strings: Strings;
  kind: SequenceKind;
  kindChoice: KindChoice;
  onKind: (value: KindChoice) => void;
  fields: Fields;
  onField: (name: keyof Fields, value: string) => void;
}) {
  const dnaFields: { name: keyof Fields; label: string }[] = [
    { name: "match", label: strings.matchLabel },
    { name: "mismatch", label: strings.mismatchLabel },
  ];
  return (
    <div className={`${CARD} space-y-4`}>
      <h2 className="font-medium text-foreground text-sm">
        {strings.scoringHeading}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={kind === "dna" ? strings.detectedDna : strings.detectedProtein}
          id="sat-kind"
          label={strings.kindLabel}
          onChange={onKind}
          options={[
            { value: "auto", label: strings.kindAuto },
            { value: "dna", label: strings.kindDna },
            { value: "protein", label: strings.kindProtein },
          ]}
          value={kindChoice}
        />
        {kind === "protein" ? (
          <SelectField
            id="sat-matrix"
            label={strings.matrixLabel}
            onChange={(value) => onField("matrix", value)}
            options={MATRIX_NAMES.map((name) => ({ value: name, label: name }))}
            value={fields.matrix}
          />
        ) : (
          dnaFields.map((field) => (
            <TextField
              id={`sat-${field.name}`}
              key={field.name}
              label={field.label}
              mono
              numeric
              onChange={(value) => onField(field.name, value)}
              value={fields[field.name]}
            />
          ))
        )}
        <TextField
          id="sat-open"
          label={strings.gapOpenLabel}
          mono
          numeric
          onChange={(value) => onField("open", value)}
          value={fields.open}
        />
        <TextField
          hint={strings.gapHint}
          id="sat-extend"
          label={strings.gapExtendLabel}
          mono
          numeric
          onChange={(value) => onField("extend", value)}
          value={fields.extend}
        />
      </div>
    </div>
  );
}

function ResultsView({
  strings,
  fmt,
  result,
  scoring,
  kind,
  mode,
  names,
  sequences,
}: {
  strings: Strings;
  fmt: Fmt;
  result: Alignment;
  scoring: Scoring;
  kind: SequenceKind;
  mode: AlignMode;
  names: readonly [string, string];
  sequences: readonly [string, string];
}) {
  const [showMatrix, setShowMatrix] = useState(false);
  const protein = kind === "protein";
  const stats = alignmentStats(result.alignedA, result.alignedB, scoring);
  const text = formatAlignment(result, names, scoring, protein);
  const matrixAllowed =
    scoring.gapOpen === scoring.gapExtend &&
    sequences[0].length <= MATRIX_LIMIT &&
    sequences[1].length <= MATRIX_LIMIT;
  const share = (count: number) =>
    t(strings.identityValue, {
      count: fmt(count),
      total: fmt(stats.columns),
      percent: percent(count, stats.columns, fmt),
    });
  const regions = [
    {
      name: names[0],
      from: result.startA + 1,
      to: result.endA,
      length: sequences[0].length,
    },
    {
      name: names[1],
      from: result.startB + 1,
      to: result.endB,
      length: sequences[1].length,
    },
  ];
  return (
    <div aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.score}
          value={fmt(result.score, { maximumFractionDigits: 2 })}
        />
        <StatCard label={strings.identity} value={share(stats.identities)} />
        {protein ? (
          <StatCard
            label={strings.similarity}
            value={share(stats.identities + stats.positives)}
          />
        ) : (
          <StatCard
            label={strings.mismatchesLabel}
            value={fmt(stats.mismatches)}
          />
        )}
        <StatCard label={strings.gaps} value={share(stats.gaps)} />
        <StatCard label={strings.columns} value={fmt(stats.columns)} />
      </StatGrid>
      {mode === "local" ? (
        <ul className="space-y-1 text-foreground text-sm">
          {regions.map((region) => (
            <li key={region.name}>
              {t(strings.regionLabel, {
                name: region.name,
                from: fmt(region.from),
                to: fmt(region.to),
                length: fmt(region.length),
              })}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.alignmentHeading}</h3>
        <AlignmentText
          label={t(strings.alignmentAria, {
            first: names[0],
            second: names[1],
          })}
          text={text}
        />
        <p className={HINT}>{protein ? strings.legend : strings.legendDna}</p>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyAlignment}
          text={text}
        />
      </div>
      <div className="space-y-2">
        {matrixAllowed ? (
          <Checkbox
            checked={showMatrix}
            label={strings.matrixToggle}
            onChange={setShowMatrix}
          />
        ) : (
          <p className={HINT}>{strings.matrixNote}</p>
        )}
        {matrixAllowed && showMatrix ? (
          <ScoreMatrix
            caption={strings.matrixCaption}
            columnLabels={["", ...sequences[1]]}
            fmt={fmt}
            grid={linearMatrix(sequences[0], sequences[1], mode, scoring)}
            path={pathCells(result)}
            rowLabels={["", ...sequences[0]]}
          />
        ) : null}
      </div>
    </div>
  );
}

function findProblem(
  strings: Strings,
  fmt: Fmt,
  input: {
    first: string;
    second: string;
    a: string;
    b: string;
    scoring: Scoring | null;
    tooLarge: boolean;
  }
): string | null {
  if (input.a === "" && input.first.trim() !== "") {
    return t(strings.errorEmpty, { n: fmt(1) });
  }
  if (input.b === "" && input.second.trim() !== "") {
    return t(strings.errorEmpty, { n: fmt(2) });
  }
  if (input.scoring === null) {
    return strings.errorNumbers;
  }
  return input.tooLarge
    ? t(strings.errorTooLarge, { limit: fmt(MAX_CELLS) })
    : null;
}

export default function SequenceAlignmentToolWidget() {
  const strings = useToolUi(SEQUENCE_ALIGNMENT_TOOL_STRINGS);
  const fmt = useToolNumber();
  const [first, setFirst] = useState(
    `>${strings.proteinHeaderA}\n${PROTEIN_A}`
  );
  const [second, setSecond] = useState(
    `>${strings.proteinHeaderB}\n${PROTEIN_B}`
  );
  const [kindChoice, setKindChoice] = useState<KindChoice>("auto");
  const [mode, setMode] = useState<AlignMode>("global");
  const [fields, setFields] = useState<Fields>(PROTEIN_FIELDS);
  const deferredFirst = useDeferredValue(first);
  const deferredSecond = useDeferredValue(second);

  const recordA = parseFasta(deferredFirst)[0];
  const recordB = parseFasta(deferredSecond)[0];
  const kind: SequenceKind =
    kindChoice === "auto"
      ? detectKind(recordA?.sequence ?? "", recordB?.sequence ?? "")
      : kindChoice;
  const cleanedA = cleanResidues(recordA?.sequence ?? "", kind);
  const cleanedB = cleanResidues(recordB?.sequence ?? "", kind);
  const scoring = buildScoring(fields, kind);
  const sequenceA = cleanedA.sequence;
  const sequenceB = cleanedB.sequence;
  const tooLarge = (sequenceA.length + 1) * (sequenceB.length + 1) > MAX_CELLS;
  const names: [string, string] = [
    recordA?.name || t(strings.unnamed, { n: fmt(1) }),
    recordB?.name || t(strings.unnamed, { n: fmt(2) }),
  ];
  const result = useMemo(
    () =>
      scoring && !tooLarge ? align(sequenceA, sequenceB, mode, scoring) : null,
    [sequenceA, sequenceB, mode, scoring, tooLarge]
  );

  function setField(name: keyof Fields, value: string) {
    setFields((previous) => ({ ...previous, [name]: value }));
  }

  function loadExample(example: "protein" | "dna") {
    const isProtein = example === "protein";
    setFirst(
      isProtein
        ? `>${strings.proteinHeaderA}\n${PROTEIN_A}`
        : `>${strings.dnaHeaderA}\nGCATGCG`
    );
    setSecond(
      isProtein
        ? `>${strings.proteinHeaderB}\n${PROTEIN_B}`
        : `>${strings.dnaHeaderB}\nGATTACA`
    );
    setFields(isProtein ? PROTEIN_FIELDS : DNA_FIELDS);
    setKindChoice("auto");
  }

  const dropped = [...new Set([...cleanedA.dropped, ...cleanedB.dropped])];
  const problem = findProblem(strings, fmt, {
    first,
    second,
    a: sequenceA,
    b: sequenceB,
    scoring,
    tooLarge,
  });
  const empty = first.trim() === "" || second.trim() === "";

  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-2">
        <TextAreaField
          hint={strings.inputHint}
          id="sat-first"
          label={strings.firstLabel}
          onChange={setFirst}
          placeholder={strings.firstPlaceholder}
          rows={6}
          value={first}
        />
        <TextAreaField
          id="sat-second"
          label={strings.secondLabel}
          onChange={setSecond}
          placeholder={strings.secondPlaceholder}
          rows={6}
          value={second}
        />
      </div>
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => loadExample("protein")}
          type="button"
        >
          {strings.exampleProtein}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => loadExample("dna")}
          type="button"
        >
          {strings.exampleDna}
        </button>
      </div>
      <PillGroup
        label={strings.modeLabel}
        name="sat-mode"
        onChange={setMode}
        options={[
          { value: "global", label: strings.modeGlobal },
          { value: "local", label: strings.modeLocal },
        ]}
        value={mode}
      />
      <ScoringPanel
        fields={fields}
        kind={kind}
        kindChoice={kindChoice}
        onField={setField}
        onKind={setKindChoice}
        strings={strings}
      />
      {dropped.length > 0 ? (
        <Notice>{t(strings.dropped, { list: dropped.join(" ") })}</Notice>
      ) : null}
      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {problem === null && empty ? <Notice>{strings.prompt}</Notice> : null}
      {result && scoring && result.alignedA === "" ? (
        <Notice>{strings.noLocal}</Notice>
      ) : null}
      {result && scoring && result.alignedA !== "" ? (
        <ResultsView
          fmt={fmt}
          kind={kind}
          mode={mode}
          names={names}
          result={result}
          scoring={scoring}
          sequences={[sequenceA, sequenceB]}
          strings={strings}
        />
      ) : null}
    </div>
  );
}
